import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bread } from "@/components/Bread";
import { CompletionScreen } from "@/components/CompletionScreen";
import { POS } from "@/components/POS";
import { Pronounce } from "@/components/Pronounce";
import { ProgressBar } from "@/components/ProgressBar";
import { useReviewQueue, type ReviewItem } from "@/hooks/useReviewQueue";
import { pad, pad3 } from "@/lib/format";
import { BOX_DAYS, MAX_BOX, dueLabel, nextBox, type Grade } from "@/lib/srs";
import { review } from "@/lib/study";

const RATINGS: ReadonlyArray<{
  id: Grade;
  num: string;
  label: string;
}> = [
  { id: "again", num: "1", label: "Again" },
  { id: "hard", num: "2", label: "Hard" },
  { id: "good", num: "3", label: "Good" },
  { id: "easy", num: "4", label: "Easy" },
];

export function Review() {
  const queue = useReviewQueue();

  if (queue.status === "loading") {
    return (
      <div className="container" style={{ paddingTop: "var(--s-8)" }}>
        <div className="empty">Building your review queue…</div>
      </div>
    );
  }
  if (queue.status === "error") {
    return (
      <div className="container" style={{ paddingTop: "var(--s-8)" }}>
        <div
          className="empty"
          style={{ borderColor: "var(--accent-red)", color: "var(--accent-red)" }}
        >
          Could not build the review queue. {queue.error?.message}
        </div>
      </div>
    );
  }
  return <Body items={queue.items} onReload={queue.reload} />;
}

interface Scored {
  again: number;
  hard: number;
  good: number;
  easy: number;
}

const NO_SCORE: Scored = { again: 0, hard: 0, good: 0, easy: 0 };

function Body({ items, onReload }: { items: readonly ReviewItem[]; onReload: () => void }) {
  const navigate = useNavigate();
  // The session owns its own queue: a word rated "Again" is pushed to the back
  // so it comes round once more before the session ends, exactly as its 10-minute
  // interval promises.
  const [session, setSession] = useState<readonly ReviewItem[]>(items);
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [scored, setScored] = useState<Scored>(NO_SCORE);
  const [done, setDone] = useState(false);

  const w = session[i];

  const rate = useCallback(
    (grade: Grade) => {
      const item = session[i];
      if (!item) return;
      review(item.day, item.index, grade);
      setScored((s) => ({ ...s, [grade]: s[grade] + 1 }));
      setFlipped(false);
      const requeue = grade === "again";
      setTimeout(() => {
        if (requeue) setSession((prev) => [...prev, item]);
        if (i + 1 >= session.length + (requeue ? 1 : 0)) setDone(true);
        else setI(i + 1);
      }, 200);
    },
    [i, session]
  );

  // 1–4 rate the card once it's face-up; SPACE flips it.
  useEffect(() => {
    if (done || !w) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === "Space") {
        e.preventDefault();
        setFlipped((f) => !f);
        return;
      }
      if (!flipped) return;
      const hit = RATINGS.find((r) => r.num === e.key);
      if (hit) {
        e.preventDefault();
        rate(hit.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [done, w, flipped, rate]);

  function restart() {
    setI(0);
    setFlipped(false);
    setScored(NO_SCORE);
    setDone(false);
    onReload();
  }

  if (items.length === 0) {
    return (
      <div className="page">
        <div className="container" style={{ paddingTop: "var(--s-5)" }}>
          <Bread crumbs={[{ label: "Archive", to: "/" }, { label: "Today's review" }]} />
        </div>
        <section className="container" style={{ paddingTop: "var(--s-7)" }}>
          <div className="empty">
            Nothing due right now. Words come back on their own schedule — drill a day or mark
            some entries and they'll show up here when they're ripe.
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: "var(--s-5)" }}>
        <Bread crumbs={[{ label: "Archive", to: "/" }, { label: "Today's review" }]} />
      </div>

      <section className="container" style={{ paddingTop: "var(--s-5)" }}>
        <div className="row-s" style={{ marginBottom: "var(--s-4)" }}>
          <div className="stack" style={{ gap: 4 }}>
            <div className="t-eyebrow">Spaced repetition · review</div>
            <h1 className="t-display" style={{ fontSize: "var(--t-2xl)", margin: 0 }}>
              Review words <span className="italic">due today</span>.
            </h1>
          </div>
          <div className="row-c" style={{ gap: 12 }}>
            <button className="btn btn-sm" onClick={() => navigate("/")} type="button">Exit</button>
          </div>
        </div>
        <div className="row-c" style={{ gap: "var(--s-3)", marginBottom: "var(--s-6)" }}>
          <div style={{ flex: 1 }}>
            <ProgressBar value={done ? session.length : i} max={session.length} />
          </div>
          <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {pad(done ? session.length : i + 1)} / {pad(session.length)}
          </div>
        </div>

        {!done && w && (
          <ReviewCard
            item={w}
            position={i}
            total={session.length}
            flipped={flipped}
            onFlip={() => setFlipped((f) => !f)}
            onRate={rate}
          />
        )}

        {done && (
          <CompletionScreen
            title="Review complete"
            subtitle={`${session.length} cards rated · they're scheduled for their next round`}
            stats={[
              { label: "Again", value: scored.again, dot: "red" },
              { label: "Hard", value: scored.hard, dot: "amber" },
              { label: "Good", value: scored.good, dot: "moss" },
              { label: "Easy", value: scored.easy, dot: "moss" },
            ]}
            primaryAction={{ label: "Back to archive →", onClick: () => navigate("/") }}
            secondaryAction={{ label: "↻ Check for more", onClick: restart }}
          />
        )}
      </section>
    </div>
  );
}

interface ReviewCardProps {
  item: ReviewItem;
  position: number;
  total: number;
  flipped: boolean;
  onFlip: () => void;
  onRate: (grade: Grade) => void;
}

function ReviewCard({ item, position, total, flipped, onFlip, onRate }: ReviewCardProps) {
  const now = Date.now();

  return (
    <div className="flashcard-stage" style={{ padding: 0 }}>
      <div className={`flashcard ${flipped ? "is-flipped" : ""}`} onClick={onFlip}>
        <div className="flashcard__inner">
          <div className="flashcard__face">
            <div className="row-s">
              <div className="flashcard__corner-tl">Day {pad(item.day)} · Entry {pad3(item.index)}</div>
              <div className="flashcard__corner-tl">Review {pad(position + 1)}/{pad(total)}</div>
            </div>
            <div className="flashcard__main">
              <h2 className="flashcard__word">{item.word}</h2>
              <div className="flashcard__ipa">/{item.ipa}/</div>
              <POS value={item.pos} />
            </div>
            <div className="row-s">
              <div className="flashcard__corner-tl">
                <span
                  className={`dot dot--${item.record.box === 0 ? "red" : item.record.box <= 2 ? "amber" : "moss"}`}
                  style={{ marginRight: 6 }}
                />
                box {item.record.box}/{MAX_BOX} · due {dueLabel(item.record.due, now)}
              </div>
              <div className="flashcard__corner-br">Tap to flip ↻</div>
            </div>
          </div>
          <div className="flashcard__face flashcard__face--back">
            <div className="row-s">
              <div className="flashcard__corner-tl">Day {pad(item.day)} · Entry {pad3(item.index)}</div>
              <div className="flashcard__corner-tl">Verso</div>
            </div>
            <div className="flashcard__main">
              <div className="flashcard__meaning">{item.meaning_vi}</div>
            </div>
            <div className="row-s">
              <div className="flashcard__corner-tl">Rate recall ↓</div>
              <div className="flashcard__corner-br">Review {pad(position + 1)}/{pad(total)}</div>
            </div>
          </div>
        </div>
      </div>

      {flipped ? (
        <>
          <div className="srs-rate">
            {RATINGS.map((r) => (
              <button
                key={r.id}
                className={`srs-rate__btn srs-rate__btn--${r.id}`}
                onClick={() => onRate(r.id)}
                type="button"
              >
                <span className="t-mono srs-rate__key">{r.num}</span>
                <span className="srs-rate__label">{r.label}</span>
                <span className="t-mono srs-rate__hint">{gradeHint(item.record.box, r.id)}</span>
              </button>
            ))}
          </div>
          <div className="row-c" style={{ gap: 12, marginTop: 10 }}>
            <Pronounce word={item.word} size={30} />
          </div>
        </>
      ) : (
        <button className="btn" onClick={onFlip} type="button">Reveal answer</button>
      )}
    </div>
  );
}

/** What the schedule will actually do to this card — not a fixed table. */
function gradeHint(box: number, grade: Grade): string {
  const target = nextBox(box, grade);
  if (target === 0) return "10m";
  return `${BOX_DAYS[Math.min(target, MAX_BOX)]}d`;
}
